import React from 'react';
import pick from 'lodash/pick';
import isEmpty from 'lodash/isEmpty';

import {
  CategoryID,
  CategoryLocalizedName
} from '../../../../../../categories/categoriesTypes';
import { IdFilterType } from '../../../../../../../types';
import {
  ChangeLifestylesFiltersFunc,
  FetchLifestylesFilterFields,
  FetchLifestylesFilterLifestyles,
  FetchLifestylesFilters,
  LifestyleCategoryID
} from '../../../../../../lifestyles/lifestylesTypes';
import { IconsEnum } from '../../../../../../../assets/icons/types';

import { LifestylesCategoriesFilterBadge } from './components/LifestylesCategoriesFilterBadge';
import { LifestylesTypesFilterBadge } from '../../../../../../lifestyles/components/filterBadges/LifestylesTypesFilterBadge';
import { LifestylesColorsFilterBadge } from '../../../../../../lifestyles/components/filterBadges/LifestylesColorsFilterBadge';
import { LifestylesMaterialsFilterBadge } from '../../../../../../lifestyles/components/filterBadges/LifestylesMaterialsFilterBadge';
import { LifestylesTonesFilterBadge } from '../../../../../../lifestyles/components/filterBadges/LifestylesTonesFilterBadge';
import { LifestylesGammasFilterBadge } from '../../../../../../lifestyles/components/filterBadges/LifestylesGammasFilterBadge';
import { LifestylesStylesFilterBadge } from '../../../../../../lifestyles/components/filterBadges/LifestylesStylesFilterBadge';
import { BooleanFilterBadge } from '../../../../../../../helpers/filterBadges/BooleanFilterBadge';

import { PureIconButtonHelper } from '../../../../../../../helpers/buttons/PureIconButtonHelper';

import { isEveryPropEmpty } from '../../../../../../../utils/isEveryPropEmpty';

import { librariesKeys, words } from '../../../../../../../locales/keys';

const allFilterFields = [
  FetchLifestylesFilterFields.SIMILAR_LIFESTYLE_IMAGE_ID,
  FetchLifestylesFilterFields.MATERIAL_IDS,
  FetchLifestylesFilterFields.PREMIUM_CONTENT_AT,
  FetchLifestylesFilterFields.STYLE_IDS,
  FetchLifestylesFilterFields.GAMMA_ID,
  FetchLifestylesFilterFields.TONE_ID,
  FetchLifestylesFilterFields.MAIN_COLOR_IDS,
  FetchLifestylesFilterFields.BLOCKED,
  FetchLifestylesFilterFields.LIFESTYLE_TYPE_ID,
  FetchLifestylesFilterFields.FAVORITE,
  FetchLifestylesFilterFields.CURRENT_TEAM_FAVORITE,
  FetchLifestylesFilterFields.TEAM_ID_FAVORITE
];

interface ThreeDStockLifestylesFiltersAppliedFiltersProps {
  lifestylesFilters: FetchLifestylesFilters;
  changeLifestylesFilters: ChangeLifestylesFiltersFunc;
  filterLifestyles: FetchLifestylesFilterLifestyles;
  clearFilters?: () => void;
  checkedCategories: { id: CategoryID; localizedName: CategoryLocalizedName }[];
  setCheckedCategories: (
    checkedCategories: {
      id: CategoryID;
      localizedName: CategoryLocalizedName;
    }[]
  ) => void;
  defaultLifestyleCategoryId?: IdFilterType;
  defaultLifestyleParentCategoryIds?: LifestyleCategoryID[];
}

function ThreeDStockLifestylesFiltersAppliedFilters({
  lifestylesFilters,
  changeLifestylesFilters,
  filterLifestyles,
  clearFilters,
  checkedCategories,
  setCheckedCategories,
  defaultLifestyleCategoryId,
  defaultLifestyleParentCategoryIds
}: ThreeDStockLifestylesFiltersAppliedFiltersProps) {
  const withFilters =
    !isEveryPropEmpty(pick(lifestylesFilters, allFilterFields)) ||
    !isEmpty(checkedCategories);

  if (!withFilters) {
    return null;
  }

  return (
    <div className="p-4" id="applied-filters">
      <div className="flex flex-wrap -m-0.5">
        <LifestylesCategoriesFilterBadge
          changeLifestylesFilters={changeLifestylesFilters}
          setCheckedCategories={setCheckedCategories}
          checkedCategories={checkedCategories}
          defaultLifestyleCategoryId={defaultLifestyleCategoryId}
          defaultLifestyleParentCategoryIds={defaultLifestyleParentCategoryIds}
        />

        <BooleanFilterBadge
          dataGa="products-favorites-filter-badge-clear"
          filters={lifestylesFilters}
          i18nLabel={words.favorites}
          name={FetchLifestylesFilterFields.FAVORITE}
          onRemoveFilter={filterLifestyles}
        />

        <BooleanFilterBadge
          dataGa="lifestyles-current-company-favorite-filter-badge-clear"
          filters={lifestylesFilters}
          i18nLabel={words.companyFavorites}
          name={FetchLifestylesFilterFields.CURRENT_TEAM_FAVORITE}
          onRemoveFilter={filterLifestyles}
        />

        <BooleanFilterBadge
          dataGa="lifestyles-company-favorite-filter-badge-clear"
          filters={lifestylesFilters}
          i18nLabel={words.companyFavorites}
          name={FetchLifestylesFilterFields.TEAM_ID_FAVORITE}
          onRemoveFilter={filterLifestyles}
        />

        <LifestylesMaterialsFilterBadge
          filters={lifestylesFilters}
          name={FetchLifestylesFilterFields.MATERIAL_IDS}
          onRemoveFilter={filterLifestyles}
        />
        <BooleanFilterBadge
          dataGa="lifestyles-premium-filter-badge-clear"
          filters={lifestylesFilters}
          i18nLabel={librariesKeys.premium}
          name={FetchLifestylesFilterFields.PREMIUM_CONTENT_AT}
          filterField="isNotNull"
          onRemoveFilter={filterLifestyles}
        />
        <BooleanFilterBadge
          dataGa="lifestyles-free-filter-badge-clear"
          filters={lifestylesFilters}
          i18nLabel={librariesKeys.free}
          name={FetchLifestylesFilterFields.PREMIUM_CONTENT_AT}
          filterField="isNull"
          onRemoveFilter={filterLifestyles}
        />

        <LifestylesStylesFilterBadge
          filters={lifestylesFilters}
          name={FetchLifestylesFilterFields.STYLE_IDS}
          onRemoveFilter={filterLifestyles}
        />
        <LifestylesGammasFilterBadge
          filters={lifestylesFilters}
          name={FetchLifestylesFilterFields.GAMMA_ID}
          onRemoveFilter={filterLifestyles}
        />
        <LifestylesTonesFilterBadge
          filters={lifestylesFilters}
          name={FetchLifestylesFilterFields.TONE_ID}
          onRemoveFilter={filterLifestyles}
        />
        <LifestylesColorsFilterBadge
          filters={lifestylesFilters}
          name={FetchLifestylesFilterFields.MAIN_COLOR_IDS}
          onRemoveFilter={filterLifestyles}
        />
        <BooleanFilterBadge
          dataGa="lifestyles-blocked-filter-badge-clear"
          filters={lifestylesFilters}
          i18nLabel={words.blocked}
          name={FetchLifestylesFilterFields.BLOCKED}
          onRemoveFilter={filterLifestyles}
        />
        <LifestylesTypesFilterBadge
          filters={lifestylesFilters}
          name={FetchLifestylesFilterFields.LIFESTYLE_TYPE_ID}
          onRemoveFilter={filterLifestyles}
        />

        <PureIconButtonHelper
          dataGa="lifestyles-clear-filters-badge"
          className="py-1.5 pl-1.5 pr-3 space-x-1 rounded-md inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base border border-gray-300 dark:border-gray-700 shadow-sm bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 m-0.5"
          i18nText={words.clear}
          i18nTextClassName="inline"
          icon={IconsEnum.X}
          iconClassName="w-4 h-4"
          onClick={clearFilters}
        />
      </div>
    </div>
  );
}

export default ThreeDStockLifestylesFiltersAppliedFilters;
